/**
 * What a hand is aimed at, and showing which model that is.
 *
 * Grabbing answers "which model" by proximity — the one you have reached into. That works
 * beautifully for a model within arm's length and not at all for one across the room,
 * which is exactly the model you would want to fetch with a stick. So the stick takes its
 * target from where the hand is pointing instead, and the two gestures stay independent:
 * the trigger picks up what you can touch, the stick moves what you can see.
 *
 * Aim is tested against each model's bounding box rather than its triangles. A laser is
 * precise and a model is full of holes, and passing the beam cleanly through the gap in a
 * bracket while the user is plainly pointing at the bracket helps nobody. The same
 * generosity is why grabbing pads its reach.
 *
 * Knows nothing about WebXR: it takes plain objects, so it can be exercised without a
 * headset.
 */

import {
  BoxGeometry,
  EdgesGeometry,
  LineBasicMaterial,
  LineSegments,
  Box3,
  type Object3D,
  Quaternion,
  Ray,
  Vector3,
} from 'three';

/** A model a hand is aimed at, and how far away it was met. */
export interface PointedModel {
  readonly root: Object3D;
  /** Distance to the near face of its bounding box, in metres. */
  readonly distance: number;
}

export class ModelPointer {
  private targets: readonly Object3D[] = [];

  private readonly ray = new Ray();
  private readonly box = new Box3();
  private readonly meeting = new Vector3();
  private readonly facing = new Quaternion();

  /** Declare what there is to point at. */
  setTargets(targets: readonly Object3D[]): void {
    this.targets = [...targets];
  }

  /**
   * Ask which model a controller is aimed at.
   *
   * A pure query, in the same way the menu's is: it reports and changes nothing, so that
   * sampling the second hand cannot undo what the first found.
   *
   * Nearest wins. A hand inside a model's box meets it at no distance at all, which is
   * the right answer — you are pointing at the thing you are standing in.
   */
  probe(controller: Object3D): PointedModel | undefined {
    if (this.targets.length === 0) return undefined;

    this.aim(controller);

    let best: PointedModel | undefined;

    for (const root of this.targets) {
      root.updateMatrixWorld(true);
      this.box.setFromObject(root);
      if (this.box.isEmpty()) continue;
      if (!this.ray.intersectBox(this.box, this.meeting)) continue;

      // From inside the box three reports where the ray leaves it, which would draw the
      // pointer out through the far side of a model the hand is already inside. There is
      // no distance to a thing you are standing in.
      const distance = this.box.containsPoint(this.ray.origin)
        ? 0
        : this.ray.origin.distanceTo(this.meeting);

      if (!best || distance < best.distance) best = { root, distance };
    }

    return best;
  }

  /**
   * How far a hand can point before the beam lands on the ground.
   *
   * `Infinity` for a hand pointing level or upwards, and for one somehow below the floor,
   * both of which mean the ground is not in the way of anything.
   *
   * The floor is part of the room and the pointer is drawn over everything in it, so a
   * beam that carries on past the ground is drawn lying across it — a line on the floor
   * that reads as a thing in the room rather than as the end of a pointer. A hand at rest
   * points downwards, so that is not a rare view of it.
   */
  floorReach(controller: Object3D): number {
    this.aim(controller);

    const { y } = this.ray.origin;
    const descent = -this.ray.direction.y;
    return descent <= 0 || y <= 0 ? Infinity : y / descent;
  }

  /** Point the shared ray down the controller's pointing axis. */
  private aim(controller: Object3D): void {
    // The runtime poses the controller, but nothing propagates that to `matrixWorld`
    // until the renderer walks the graph, which happens after this runs.
    controller.updateMatrixWorld(true);
    this.ray.origin.setFromMatrixPosition(controller.matrixWorld);
    this.ray.direction
      .set(0, 0, -1)
      .applyQuaternion(controller.getWorldQuaternion(this.facing))
      .normalize();
  }
}

/**
 * Colour of the outline around the model a hand is pointing at.
 *
 * Two of them, because the outline is the only thing saying whether what you are about to
 * move is still the size the file says it is. At true size it matches the pointer; away
 * from true size it turns amber, so a model you have grown is visibly a model you can no
 * longer measure your hand against.
 */
const TRUE_SIZE_COLOUR = 0x4da3ff;
const RESIZED_COLOUR = 0xffb454;

/**
 * Gap between the model's surface and the outline, as a fraction of its largest dimension.
 *
 * Proportional rather than fixed. A centimetre of clearance is invisible round a wardrobe
 * and a tenth of the width of a bottle cap, and a box standing well clear of a small model
 * reads as a container it happens to be sitting in rather than as a box drawn round it.
 */
const OUTLINE_MARGIN = 0.04;

/** Limits on that gap, in metres: enough to see round, never enough to look detached. */
const MIN_OUTLINE_MARGIN_M = 0.003;
const MAX_OUTLINE_MARGIN_M = 0.04;

/**
 * A box drawn round the model a hand is pointing at.
 *
 * One per hand: both hands can be aimed at different models and each moves the one it is
 * aimed at, so a single shared outline would tell only half the story.
 *
 * Depth-tested, unlike the pointer ray. The box encloses the model, so its far edges are
 * genuinely behind the thing it is drawn round, and a box whose back edges lie over the
 * model reads as a flat decoration pasted on top rather than as a volume the model is
 * standing inside. Letting the model occlude what is behind it is what makes it a box.
 */
export class Outline {
  readonly object: LineSegments<EdgesGeometry, LineBasicMaterial>;

  private readonly box = new Box3();
  private readonly centre = new Vector3();
  private readonly size = new Vector3();

  constructor() {
    const material = new LineBasicMaterial({
      color: TRUE_SIZE_COLOUR,
      transparent: true,
      opacity: 0.85,
    });

    this.object = new LineSegments(new EdgesGeometry(new BoxGeometry(1, 1, 1)), material);
    this.object.visible = false;
  }

  /**
   * Fit the outline round a model.
   *
   * @param trueSize whether the model is currently the size its file says it is.
   */
  show(root: Object3D, trueSize: boolean): void {
    root.updateMatrixWorld(true);
    this.box.setFromObject(root);

    if (this.box.isEmpty()) {
      this.hide();
      return;
    }

    this.box.getCenter(this.centre);
    this.box.getSize(this.size);

    const margin = Math.min(
      MAX_OUTLINE_MARGIN_M,
      Math.max(
        MIN_OUTLINE_MARGIN_M,
        Math.max(this.size.x, this.size.y, this.size.z) * OUTLINE_MARGIN,
      ),
    );

    this.object.position.copy(this.centre);
    this.object.scale.set(
      this.size.x + margin * 2,
      this.size.y + margin * 2,
      this.size.z + margin * 2,
    );
    this.object.material.color.setHex(trueSize ? TRUE_SIZE_COLOUR : RESIZED_COLOUR);
    this.object.visible = true;
  }

  hide(): void {
    this.object.visible = false;
  }

  dispose(): void {
    this.object.geometry.dispose();
    this.object.material.dispose();
  }
}
